import { useState } from 'react'
import styled from '@emotion/styled'
import axios from 'axios'
import cx from 'classnames'
import { useTranslation, Trans } from 'next-i18next'

import Layout from '@/components/Layout/Layout'
import SEO from '@/components/Layout/SEO'
import Button from '@/elements/button/Button'
import config from '@/config'
import { getServerSideTranslationProps } from '@/utils/translation'
import { ReCaptchaWrapper } from '@/components/ReCaptcha/ReCaptchaWrapper'
import SelectWithIconIndicator from '../components/Form/components/CustomSelects/SelectWithIconIndicator'

const { API_URL, SUPPORT_EMAIL } = config

const ContactPageContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    margin: 4rem 2rem;

    h2 {
        font-weight: 600;
        margin: 0 0 4rem;
        text-align: center;
    }

    p {
        margin: 0.25rem;
    }

    button {
        height: 2.75rem;
        margin: 1rem 0 0;
    }

    @media (min-width: ${props => props.theme.bps.md}) {
        padding: 0;
        min-height: calc(100vh - 6.75rem);

        button {
            margin: 0;
        }
    }

    .side-note {
        text-align: center;
        margin: 0 2.5rem;
        font-size: 0.85rem;
        &.contact-us {
            font-weight: 600;
        }
    }
`

const ContactUsContent = styled.div`
    width: 100%;
    max-width: 45rem;
    margin: 2.5rem;
    padding: 1.5rem;

    border-radius: 12px;
    border: 1px solid ${props => props.theme.colors.border};

    box-shadow: 0 0 31px -3px rgba(179, 179, 179, 0.5);
    -webkit-box-shadow: 0 0 31px -3px rgba(179, 179, 179, 0.5);
    -moz-box-shadow: 0 0 31px -3px rgba(179, 179, 179, 0.5);

    background-color: ${props => props.theme.colors.white};

    button {
        text-transform: capitalize;
    }

    .form-actions {
        width: 100%;
        display: flex;
        gap: 1.5rem;
        justify-content: center;
        align-items: center;
    }

    @media (min-width: ${props => props.theme.bps.md}) {
        width: initial;
        padding: 1.5rem 3rem 3rem;
    }
`

const ContactForm = styled.form``

const ContactFormGrid = styled.div`
    display: grid;
    margin: 1.5rem 0;
    @media (min-width: ${props => props.theme.bps.md}) {
        grid-template: repeat(2, 4rem) 1fr / repeat(2, 1fr);
        grid-gap: 3rem 2.5rem;

        .message {
            grid-area: 4 / 1 / 4 / 3;
        }

        .select {
            grid-area: 3 / 1 / 4 / 1;
        }
    }
`

const FormInput = styled.div`
    label {
        margin-left: 1rem;
        font-weight: 600;
        font-size: 0.9rem;
    }

    input,
    textarea {
        width: 100%;
        margin: 0.25rem 0;
        padding: 1rem 1.5rem;

        border: 1px solid ${props => props.theme.colors.border};
        border-radius: 10px;
    }

    input {
        height: 4rem;
    }
`

const FormSelect = styled.div`
    label {
        margin-left: 1rem;
        font-weight: 600;
        font-size: 0.9rem;
    }
`

const CaptchaContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    width: 100%;
    margin: 1.5rem 0;
`

const Message = styled.div<{ status: string }>`
    color: ${({ status, theme }) =>
        status === 'success' ? theme.colors.success : theme.colors.error};
    font-weight: bold;
    border-bottom: 4px solid
        ${({ status, theme }) => (status === 'success' ? theme.colors.success : theme.colors.error)};
    margin-top: 1rem;
    padding-bottom: 0.5rem;
    display: none;

    &.show {
        display: block;
    }
`

const initialUserDetails = {
    name: '',
    email: '',
    subject: '',
    message: '',
}

const ContactPage = () => {
    const { t } = useTranslation('auth')

    const [userDetails, setUserDetails] = useState(initialUserDetails)
    const [status, setStatus] = useState('pending')
    const [error, setError] = useState({})
    const [type, setType] = useState(null)
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
    const typeOptions = [
        {
            label: "Support",
            value: "Support",
        },
        {
            label: "Compliance/Legal",
            value: "Compliance/Legal",
        },
        {
            label: "Other",
            value: "Other",
        },
    ]

    const handleFormChange = ({ target: { name, value } }) => {
        setUserDetails(prevState => ({ ...prevState, [name]: value }))
    }

    const handleSubmit = e => {
        e.preventDefault()
        if (!recaptchaToken) {
            setStatus('error')
            return
        }

        const { subject } = userDetails
        setStatus('submitting')

        axios
            .post(`${API_URL}/support/`, {
                ...userDetails,
                subject: type?.value ? `[${type.value}]  ${subject}` : subject,
                type: type?.value || 'Support',
                recaptchaToken,
            })
            .then(() => {
                setUserDetails(initialUserDetails)
                setStatus('success')
            })
            .catch(err => {
                // TODO: Show errors on frontend and do same basic regexp testing for values on frontend.
                if (err.response?.data) {
                    setError(err.response?.data)
                } else {
                    setError({ form: true })
                }
                setStatus('error')
            })
            .finally(() => {
                setTimeout(() => {
                    setStatus('pending')
                }, 10000)
            })
    }

    return (
        <Layout>
            <SEO title="Contact Us" description="contact us" />

            <ContactPageContainer>
                <h2 className="color-primary">{t('contact.title')}</h2>

                <p>
                    <Trans t={t} i18nKey="contact.supportContacts" values={{supportEmail: SUPPORT_EMAIL}} components={{
                        supportEmail: <a className="color-primary" href={`mailto=${SUPPORT_EMAIL}`}/>
                    }}/>
                </p>

                <div className="side-note contact-us">
                    {t('contact.contactUs')}
                </div>

                <ContactUsContent>
                    <ContactForm onSubmit={handleSubmit}>
                        <ContactFormGrid>
                            <FormInput>
                                <label htmlFor="name">Full Name*</label>

                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    maxLength={50}
                                    required
                                    value={userDetails.name}
                                    onChange={handleFormChange}
                                />
                            </FormInput>

                            <FormInput>
                                <label htmlFor="subject">{t('contact.subject')}*</label>

                                <input
                                    id="subject"
                                    type="text"
                                    maxLength={50}
                                    name="subject"
                                    value={userDetails.subject}
                                    onChange={handleFormChange}
                                />
                            </FormInput>

                            <FormInput>
                                <label htmlFor="email">{t('common.email')}*</label>

                                <input
                                    id="email"
                                    type="email"
                                    maxLength={50}
                                    name="email"
                                    placeholder="email@example.com"
                                    required
                                    value={userDetails.email}
                                    onChange={handleFormChange}
                                />
                            </FormInput>

                            <FormSelect>
                                <label htmlFor="select">{t('common.type')}</label>
                                <SelectWithIconIndicator
                                    className='select'
                                    id="type"
                                    placeholder="Select type"
                                    options={typeOptions}
                                    selectedOption={type}
                                    name="type"
                                    // @ts-ignore
                                    onChange={e => setType(e)}
                                />
                            </FormSelect>

                            <FormInput className="message">
                                <label htmlFor="message">{t('contact.message')}</label>

                                <textarea
                                    id="name"
                                    name="message"
                                    rows={4}
                                    maxLength={255}
                                    required
                                    value={userDetails.message}
                                    onChange={handleFormChange}
                                />
                            </FormInput>
                        </ContactFormGrid>

                        <CaptchaContainer>
                            <ReCaptchaWrapper
                                onVerify={setRecaptchaToken}
                                onExpire={() => setRecaptchaToken(null)}
                            />
                        </CaptchaContainer>

                        <div className="form-actions">
                            <Button type="submit" disabled={status === 'submitting' || !recaptchaToken}>
                                {status === 'submitting'
                                    ? t('contact.submitting')
                                    : t('contact.submit')}
                            </Button>
                        </div>
                    </ContactForm>

                    <Message status={status} className={cx(status === 'success' ? 'show' : null)}>
                        {t('contact.success')}
                    </Message>

                    <Message status={status} className={cx(status === 'error' ? 'show' : null)}>
                        {/* @ts-ignore */}
                        {error.email ? (
                            // @ts-ignore
                            error.email
                        ) : (
                            <Trans t={t} i18nKey="contact.error">
                                Something went wrong, please try again later or email us at{' '}
                                <a className="color-primary" href={`mailto=${SUPPORT_EMAIL}`}>
                                    {/* @ts-ignore */}
                                    {{SUPPORT_EMAIL}}
                                </a>
                                .
                            </Trans>
                        )}
                    </Message>
                </ContactUsContent>

                <div className="side-note">
                    <p>
                        For individuals looking for customer support, please use the chatbox and our
                        24/7 customer service will assist you.
                    </p>
                </div>
            </ContactPageContainer>
        </Layout>
    )
}

const i18nNamespaces = ['auth', 'errors']

export const getStaticProps = async ({ locale }) => {
    return {
        props: {
            ...(await getServerSideTranslationProps({ locale: 'en', i18nNamespaces })),
        },
    }
}

export default ContactPage
